// crewbit-admin/src/components/schedule/WeekGrid.tsx
//
// Schedule Module Phase C C2 — 7-column day grid for admin Week view.
// Composes <WeekDayColumn> per day. Cross-column drag = reschedule (date
// change, time preserved per spec §3.8 — Δdays applied at consumer).
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3.3.

import { admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import { WeekDayColumn } from './WeekDayColumn';

export interface WeekGridProps {
  days: string[];
  entriesByDate: Record<string, ScheduleEntry[]>;
  onEntryTap: (entryId: string) => void;
  /** Cross-column drop = date change. Caller handles Δdays + RPC dispatch. */
  onColumnDrop?: (date: string, droppedEntryId: string) => void;
  onEmptySlotCreate?: (date: string) => void;
  industryAccent?: string;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function WeekGrid({
  days,
  entriesByDate,
  onEntryTap,
  onColumnDrop,
  onEmptySlotCreate,
  industryAccent,
}: WeekGridProps) {
  const today = todayIso();
  return (
    <div
      data-testid="week-grid"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        padding: 16,
        overflowX: 'auto',
        backgroundColor: admin.bg,
      }}
    >
      {days.map((date) => (
        <WeekDayColumn
          key={date}
          date={date}
          entries={entriesByDate[date] ?? []}
          isToday={date === today}
          onEntryTap={onEntryTap}
          onColumnDrop={onColumnDrop}
          onEmptySlotCreate={onEmptySlotCreate}
          industryAccent={industryAccent}
          onEntryDragStart={onColumnDrop ? (entry, e) => {
            e.dataTransfer.setData('text/entry-id', entry.id);
            e.dataTransfer.effectAllowed = 'move';
          } : undefined}
        />
      ))}
    </div>
  );
}
